import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { copyTextToSystemClipboard } from "@excalidraw/excalidraw/clipboard";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import { TextField } from "@excalidraw/excalidraw/components/TextField";
import {
  copyIcon,
  LinkIcon,
  playerPlayIcon,
  playerStopFilledIcon,
  share,
  shareIOS,
  shareWindows,
} from "@excalidraw/excalidraw/components/icons";
import { useUIAppState } from "@excalidraw/excalidraw/context/ui-appState";
import { useCopyStatus } from "@excalidraw/excalidraw/hooks/useCopiedIndicator";
import { KEYS, getFrame } from "@excalidraw/common";
import { useEffect, useRef, useState } from "react";

import { atom, useAtom, useAtomValue } from "../app-jotai";
import { activeRoomLinkAtom } from "../collab/Collab";
import {
  apiAddCollaborators,
  apiCreateShareLink,
  type Permission,
  type SearchedUser,
} from "../data/api";

import "./ShareDialog.scss";
import { QRCode } from "./QRCode";
import { UserPicker } from "./UserPicker";
import { CollaboratorList } from "./CollaboratorList";

import type { CollabAPI } from "../collab/Collab";

type OnExportToBackend = () => void;
type ShareDialogType = "share" | "collaborationOnly";

const MAX_MESSAGE_LENGTH = 500;

export const shareDialogStateAtom = atom<
  { isOpen: false } | { isOpen: true; type: ShareDialogType }
>({ isOpen: false });

const getShareIcon = () => {
  const navigator = window.navigator as any;
  const isAppleBrowser = /Apple/.test(navigator.vendor);
  const isWindowsBrowser = navigator.appVersion.indexOf("Win") !== -1;

  if (isAppleBrowser) {
    return shareIOS;
  } else if (isWindowsBrowser) {
    return shareWindows;
  }

  return share;
};

export type ShareDialogProps = {
  collabAPI: CollabAPI | null;
  handleClose: () => void;
  onExportToBackend: OnExportToBackend;
  type: ShareDialogType;
  activeDrawingId: string | null;
};

// Subcomponente: seção de convite de usuários internos. Só faz sentido quando
// o drawing está salvo na nuvem (i.e. tem activeDrawingId).
//
// Oferece apenas as permissões 'view' e 'edit'. Para colaboração ao vivo o
// usuário usa o botão nativo "Iniciar sessão" deste mesmo diálogo.
const InviteUsersSection = ({
  drawingId,
  onSent,
}: {
  drawingId: string;
  onSent: () => void;
}) => {
  const [selected, setSelected] = useState<SearchedUser[]>([]);
  const [permission, setPermission] = useState<Permission>("view");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const canSend = selected.length > 0 && !sending;

  const handleSubmit = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    setSuccess(false);

    try {
      await apiAddCollaborators(drawingId, {
        userIds: selected.map((u) => u.id),
        permission,
        message: message.trim() || undefined,
      });

      setSuccess(true);
      setSelected([]);
      setMessage("");
      onSent();
    } catch (err: any) {
      setError(err?.message ?? "Falha ao enviar.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="ShareDialog__invite">
      <div className="ShareDialog__picker__header">
        Compartilhar com usuários
      </div>
      <div className="ShareDialog__picker__description">
        Selecione quem deve receber este desenho. O destinatário recebe uma
        notificação com as opções habilitadas pela permissão escolhida.
      </div>

      <UserPicker
        value={selected}
        onChange={setSelected}
        disabled={sending}
        allowSelectAll
      />

      <div className="ShareDialog__invite__permission">
        <label>
          <input
            type="radio"
            name="permission"
            value="edit"
            checked={permission === "edit"}
            onChange={() => setPermission("edit")}
            disabled={sending}
          />
          <span>
            <strong>Edição</strong> — pode salvar cópia ou visualizar
          </span>
        </label>
        <label>
          <input
            type="radio"
            name="permission"
            value="view"
            checked={permission === "view"}
            onChange={() => setPermission("view")}
            disabled={sending}
          />
          <span>
            <strong>Visualização</strong> — pode apenas visualizar
          </span>
        </label>
      </div>

      <textarea
        className="ShareDialog__invite__message"
        placeholder="Mensagem opcional para o destinatário"
        maxLength={MAX_MESSAGE_LENGTH}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        disabled={sending}
        rows={2}
      />

      {error && <div className="ShareDialog__invite__error">{error}</div>}
      {success && (
        <div className="ShareDialog__invite__success">
          Convite enviado com sucesso.
        </div>
      )}

      <div className="ShareDialog__picker__button">
        <FilledButton
          size="large"
          label={sending ? "Enviando..." : "Enviar"}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};

const ActiveRoomDialog = ({
  collabAPI,
  activeRoomLink,
  handleClose,
}: {
  collabAPI: CollabAPI;
  activeRoomLink: string;
  handleClose: () => void;
}) => {
  const [, setJustCopied] = useState(false);
  const timerRef = useRef<number>(0);
  const ref = useRef<HTMLInputElement>(null);
  const isShareSupported = "share" in navigator;
  const { onCopy, copyStatus } = useCopyStatus();

  const copyRoomLink = async () => {
    try {
      await copyTextToSystemClipboard(activeRoomLink);
    } catch (e) {
      collabAPI.setCollabError("Falha ao copiar para a área de transferência.");
    }

    setJustCopied(true);

    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
    }

    timerRef.current = window.setTimeout(() => {
      setJustCopied(false);
    }, 3000);

    ref.current?.select();
  };

  const shareRoomLink = async () => {
    try {
      await navigator.share({
        title: "Excalidraw",
        text: "Excalidraw",
        url: activeRoomLink,
      });
    } catch (error: any) {
      // Just ignore.
    }
  };

  return (
    <>
      <h3 className="ShareDialog__active__header">Colaboração ao vivo</h3>
      <TextField
        defaultValue={collabAPI.getUsername()}
        placeholder="Seu nome"
        label="Seu nome"
        onChange={collabAPI.setUsername}
        onKeyDown={(event) => event.key === KEYS.ENTER && handleClose()}
      />
      <div className="ShareDialog__active__linkRow">
        <TextField
          ref={ref}
          label="Link"
          readonly
          fullWidth
          value={activeRoomLink}
        />
        {isShareSupported && (
          <FilledButton
            size="large"
            variant="icon"
            label="Compartilhar"
            icon={getShareIcon()}
            className="ShareDialog__active__share"
            onClick={shareRoomLink}
          />
        )}
        <FilledButton
          size="large"
          label="Copiar link"
          icon={copyIcon}
          status={copyStatus}
          onClick={() => {
            copyRoomLink();
            onCopy();
          }}
        />
      </div>
      <QRCode value={activeRoomLink} />
      <div className="ShareDialog__active__description">
        <p>
          <span
            role="img"
            aria-hidden="true"
            className="ShareDialog__active__description__emoji"
          >
            🔒{" "}
          </span>
          Não se preocupe, a sessão é criptografada de ponta a ponta e
          totalmente privada. Nem o nosso servidor consegue ver o que você
          desenha.
        </p>
        <p>
          Encerrar a sessão desconecta você dos outros colaboradores, mas você
          pode continuar trabalhando offline.
        </p>
      </div>

      <div className="ShareDialog__active__actions">
        <FilledButton
          size="large"
          variant="outlined"
          color="danger"
          label="Encerrar sessão"
          icon={playerStopFilledIcon}
          onClick={() => {
            trackEvent("share", "room closed");
            collabAPI.stopCollaboration();
            if (!collabAPI.isCollaborating()) {
              handleClose();
            }
          }}
        />
      </div>
    </>
  );
};

// Subcomponente: seção "Link de visualização" — reutiliza apiCreateShareLink
// (nosso backend) em vez do exportToBackend original do Excalidraw (que aponta
// para um servidor da Excalidraw que não funciona mais para forks).
const ViewLinkSection = ({ drawingId }: { drawingId: string }) => {
  const { onCopy, copyStatus } = useCopyStatus();
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setError(null);
    try {
      const share = await apiCreateShareLink(drawingId);
      const url = `${window.location.origin}/#view=${share.share_token}`;
      await copyTextToSystemClipboard(url);
      onCopy();
    } catch (err: any) {
      setError(err?.message ?? "Falha ao gerar link.");
    }
  };

  return (
    <>
      <div className="ShareDialog__picker__header">Link de visualização</div>
      <div className="ShareDialog__picker__description">
        Gera um link somente leitura para compartilhar com qualquer pessoa.
      </div>

      <div className="ShareDialog__picker__button">
        <FilledButton
          size="large"
          label="Gerar e copiar link"
          icon={LinkIcon}
          status={copyStatus}
          onClick={handleGenerate}
        />
      </div>

      {error && <div className="ShareDialog__invite__error">{error}</div>}
    </>
  );
};

const ShareDialogPicker = (props: ShareDialogProps) => {
  const { collabAPI } = props;
  // Incrementado a cada convite enviado, para recarregar a lista de acesso.
  const [reloadToken, setReloadToken] = useState(0);

  const startCollabJSX = collabAPI ? (
    <>
      <div className="ShareDialog__picker__header">Colaboração ao vivo</div>

      <div className="ShareDialog__picker__description">
        <div style={{ marginBottom: "1em" }}>
          Convide pessoas para colaborar no seu desenho.
        </div>
        Não se preocupe, a sessão é criptografada de ponta a ponta e totalmente
        privada. Nem o nosso servidor consegue ver o que você desenha.
      </div>

      <div className="ShareDialog__picker__button">
        <FilledButton
          size="large"
          label="Iniciar sessão"
          icon={playerPlayIcon}
          onClick={() => {
            trackEvent("share", "room creation", `ui (${getFrame()})`);
            collabAPI.startCollaboration(null);
          }}
        />
      </div>

      {props.type === "share" && (
        <div className="ShareDialog__separator">
          <span>ou</span>
        </div>
      )}
    </>
  ) : null;

  return (
    <>
      {startCollabJSX}

      {props.type === "share" && props.activeDrawingId && (
        <>
          <ViewLinkSection drawingId={props.activeDrawingId} />

          <div className="ShareDialog__separator">
            <span>ou</span>
          </div>

          <InviteUsersSection
            drawingId={props.activeDrawingId}
            onSent={() => setReloadToken((t) => t + 1)}
          />

          <CollaboratorList
            drawingId={props.activeDrawingId}
            reloadToken={reloadToken}
          />
        </>
      )}
    </>
  );
};

const ShareDialogInner = (props: ShareDialogProps) => {
  const activeRoomLink = useAtomValue(activeRoomLinkAtom);

  // Aumenta a dialog quando há seção de convite (mais conteúdo).
  const size = props.type === "share" && props.activeDrawingId ? "regular" : "small";

  return (
    <Dialog size={size} onCloseRequest={props.handleClose} title={false}>
      <div className="ShareDialog">
        {props.collabAPI && activeRoomLink ? (
          <ActiveRoomDialog
            collabAPI={props.collabAPI}
            activeRoomLink={activeRoomLink}
            handleClose={props.handleClose}
          />
        ) : (
          <ShareDialogPicker {...props} />
        )}
      </div>
    </Dialog>
  );
};

export const ShareDialog = (props: {
  collabAPI: CollabAPI | null;
  onExportToBackend: OnExportToBackend;
  activeDrawingId: string | null;
}) => {
  const [shareDialogState, setShareDialogState] = useAtom(shareDialogStateAtom);

  const { openDialog } = useUIAppState();

  useEffect(() => {
    if (openDialog) {
      setShareDialogState({ isOpen: false });
    }
  }, [openDialog, setShareDialogState]);

  if (!shareDialogState.isOpen) {
    return null;
  }

  return (
    <ShareDialogInner
      handleClose={() => setShareDialogState({ isOpen: false })}
      collabAPI={props.collabAPI}
      onExportToBackend={props.onExportToBackend}
      type={shareDialogState.type}
      activeDrawingId={props.activeDrawingId}
    />
  );
};
